import { Controller, FormProvider } from 'react-hook-form';
import { StanButton, StanInput, StanPhoneInput } from '@/components';
import { PhoneChange } from '@/components/core-components/StanPhoneInput/StanPhoneInput.types';
import { useAffiliateRegistrationForm } from '../hooks/useAffiliateRegistrationForm';
import IconPerson from '@/assets/icons/ic-profile.svg';
import IconMail from '@/assets/icons/ic-mail.svg';
import IconLock from '@/assets/icons/ic-lock.svg';
import type { AffiliateUser } from '@/lib/api/auth/types';

interface AffiliateRegistrationFormProps {
    /** Pre-filled affiliate user data from token */
    affiliateUser: AffiliateUser | undefined;
    /** JWT token for API authorization */
    token: string;
    /** Callback when registration succeeds */
    onSuccess: () => void;
    /** Override default country for phone input (ISO 3166-1 alpha-2 code) */
    defaultCountry?: string;
}

export function AffiliateRegistrationForm({
    affiliateUser,
    token,
    onSuccess,
    defaultCountry,
}: AffiliateRegistrationFormProps) {
    const { form, onSubmit, isSubmitting } = useAffiliateRegistrationForm({
        affiliateUser,
        token,
        onSuccess,
    });

    const {
        control,
        formState: { errors },
        setValue,
    } = form;

    const handlePhoneChange = (value: PhoneChange) => {
        setValue('phoneNumber', value.e164, { shouldValidate: true });
        if (value.country) {
            setValue('countryCode', value.country, { shouldValidate: true });
        }
    };

    return (
        <FormProvider {...form}>
            <form onSubmit={onSubmit} className='space-y-2.5'>
                {/* Full Name Input - Disabled, pre-filled from token */}
                <Controller
                    name='fullName'
                    control={control}
                    render={({ field }) => (
                        <StanInput
                            id='fullName'
                            name={field.name}
                            type='text'
                            placeholder='Your Name'
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled
                            error={!!errors.fullName}
                            hintText={errors.fullName?.message}
                            startIcon={IconPerson}
                        />
                    )}
                />

                {/* Email Input - Disabled, pre-filled from token */}
                <Controller
                    name='email'
                    control={control}
                    render={({ field }) => (
                        <StanInput
                            id='email'
                            name={field.name}
                            type='email'
                            placeholder='Email'
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled
                            error={!!errors.email}
                            hintText={errors.email?.message}
                            startIcon={IconMail}
                        />
                    )}
                />

                {/* Phone Number Input */}
                <Controller
                    name='phoneNumber'
                    control={control}
                    render={({ field }) => (
                        <StanPhoneInput
                            id='phoneNumber'
                            name={field.name}
                            placeholder='Phone Number'
                            value={field.value}
                            onChange={handlePhoneChange}
                            error={!!errors.phoneNumber}
                            hintText={errors.phoneNumber?.message}
                            defaultCountry={defaultCountry}
                        />
                    )}
                />

                {/* Password Input */}
                <Controller
                    name='password'
                    control={control}
                    render={({ field }) => (
                        <StanInput
                            id='password'
                            name={field.name}
                            type='password'
                            placeholder='Password'
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={isSubmitting}
                            error={!!errors.password}
                            hintText={errors.password?.message}
                            startIcon={IconLock}
                        />
                    )}
                />

                {/* Submit Button */}
                <div className='pt-6'>
                    <StanButton
                        type='submit'
                        name='affiliate-register-submit'
                        color='primary'
                        className='w-full'
                        disabled={isSubmitting}
                        loading={isSubmitting}
                    >
                        Start Reselling
                    </StanButton>
                </div>
            </form>
        </FormProvider>
    );
}
